import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createAllyProduct, updateAllyProduct, getOneAllyProduct } from '../../services/adminService';
import ImageUploadField from '../../components/ImageUploadField';

const ALLY_IMAGE_BUCKET = 'products_images';

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.35rem',
};

const AdminAllyProductForm = () => {
  const { id: allyId, productId } = useParams();
  const isNew = !productId || productId === 'new';
  const { session } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', artist: '', description: '', price: '', image: '', slug: '',
    display_order: '', is_active: true, is_sold_out: false, mercado_pago_url: '',
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Load product in edit mode via direct single-product GET
  useEffect(() => {
    if (isNew || !session?.access_token) return;
    let cancelled = false;

    getOneAllyProduct(session.access_token, productId)
      .then((data) => {
        if (cancelled) return;
        const p = data.product;
        if (p) {
          setForm({
            name: p.name || '',
            artist: p.artist || '',
            description: p.description || '',
            price: p.price ?? '',
            image: p.image || '',
            slug: p.slug || '',
            display_order: p.display_order ?? '',
            is_active: p.is_active !== false,
            is_sold_out: Boolean(p.is_sold_out),
            mercado_pago_url: p.mercado_pago_url || '',
          });
        } else {
          setError('Producto no encontrado.');
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) { setError(err.message); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [productId, isNew, session?.access_token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        ally_id: parseInt(allyId, 10),
        name: form.name.trim(),
        artist: form.artist.trim() || null,
        description: form.description.trim() || null,
        price: parseFloat(form.price),
        image: form.image.trim() || null,
        slug: form.slug.trim() || null,
        display_order: form.display_order !== '' ? parseInt(form.display_order, 10) : null,
        is_active: form.is_active,
        is_sold_out: form.is_sold_out,
        mercado_pago_url: form.mercado_pago_url.trim() || null,
      };

      if (isNew) {
        await createAllyProduct(session.access_token, payload);
      } else {
        await updateAllyProduct(session.access_token, productId, payload);
      }

      navigate(`/admin/allies/${allyId}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem 0' }}>
        <p style={{ color: '#888' }}>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <span style={{ color: '#ccc' }}>/</span>
        <Link to="/admin/allies" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>Aliados</Link>
        <span style={{ color: '#ccc' }}>/</span>
        <Link to={`/admin/allies/${allyId}`} style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>Aliado</Link>
        <span style={{ color: '#ccc' }}>/</span>
        <span style={{ fontSize: '0.9rem', color: '#333' }}>{isNew ? 'Nuevo producto' : 'Editar producto'}</span>
      </div>

      <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', marginBottom: '2rem' }}>
        {isNew ? 'Nuevo producto' : 'Editar producto'}
      </h1>

      <form onSubmit={handleSubmit} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          <div>
            <label style={labelStyle}>Nombre *</label>
            <input name="name" value={form.name} onChange={handleChange} required style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Artista</label>
            <input name="artist" value={form.artist} onChange={handleChange} style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Descripción</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Edición limitada, materiales, colección…"
            />
          </div>

          <div>
            <label style={labelStyle}>Precio (MXN) *</label>
            <input name="price" type="number" min="0.01" step="0.01" value={form.price}
              onChange={handleChange} required style={{ ...inputStyle, maxWidth: 180 }} />
          </div>

          <div>
            <label htmlFor="mercado_pago_url" style={labelStyle}>Liga de pago de Mercado Pago</label>
            <input id="mercado_pago_url" name="mercado_pago_url" type="url" value={form.mercado_pago_url}
              onChange={handleChange} style={inputStyle} placeholder="https://mpago.la/…" />
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              Opcional. Si todos los productos de la bolsa tienen liga, en el checkout se ofrece "Link de Mercado Pago"
              y el cliente recibe la liga al confirmar su pedido.
            </p>
          </div>

          <ImageUploadField
            label="Imagen"
            value={form.image}
            onChange={(url) => setForm((f) => ({ ...f, image: url }))}
            bucket={ALLY_IMAGE_BUCKET}
            onUploadingChange={setUploading}
          />

          <div>
            <label style={labelStyle}>Slug</label>
            <input name="slug" value={form.slug} onChange={handleChange} style={inputStyle} placeholder="mi-producto" />
          </div>

          <div>
            <label style={labelStyle}>Orden de visualización</label>
            <input name="display_order" type="number" value={form.display_order} onChange={handleChange}
              style={{ ...inputStyle, maxWidth: 120 }} placeholder="—" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input id="is_active" name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="is_active" style={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>Activo</label>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input id="is_sold_out" name="is_sold_out" type="checkbox" checked={form.is_sold_out} onChange={handleChange} />
              <label htmlFor="is_sold_out" style={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>Agotado</label>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>
              Sigue visible en la página del aliado, pero no se puede agregar a la bolsa.
            </p>
          </div>

          {error && <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: 0 }}>{error}</p>}

          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <button type="submit" disabled={saving || uploading}
              style={{
                padding: '0.6rem 1.5rem', background: saving || uploading ? '#ccc' : '#f59e0b', color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem',
                cursor: saving || uploading ? 'default' : 'pointer',
              }}>
              {uploading ? 'Subiendo imagen...' : saving ? 'Guardando...' : isNew ? 'Crear producto' : 'Guardar cambios'}
            </button>
            <Link to={`/admin/allies/${allyId}`}
              style={{ padding: '0.6rem 1rem', color: '#888', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center' }}>
              Cancelar
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminAllyProductForm;
